import { useEffect, useState, useRef } from 'react';
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter } from '../../services/loanApi';
import { getBPCPrograms, getPageFieldVisibility } from '../../services/userApi';
import { logError } from "../../utils/logError";
import encompassFields from '../../data/encompass_fields.json';
import encompassFieldsDSCR from '../../data/encompass_fields_DSCR.json';
import AdditionalInformation from './AdditionalInformation';
import { getAdditionalInfoFields } from '../../config/organizations';
import LoadingOverlay from '../shared/LoadingOverlay';
import Toast from '../shared/Toast';

export default function CorrespondentAdditionalInfo() {
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [loanid, setLoanid] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState('Loan saved successfully...');
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");
    const [bpcPrograms, setBpcPrograms] = useState([]);
    const [hiddenAdditionalFieldIds, setHiddenAdditionalFieldIds] = useState([]);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [documentationType, setDocumentationType] = useState("Income");
    const [fieldsJson, setFieldsJson] = useState(encompassFields);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            window.elli.script.getObject("loan").then(async function (loanObject) {
                const raw = await loanObject.all();
                const loan = raw?.loan ?? raw;
                try {
                    const tpoAuthObject = await window.elli.script.getObject("auth");
                    const authCode = await tpoAuthObject.createAuthCode();
                    const token = await getAccessToken(authCode);
                    // Use fieldReader to get 2626 value
                    const data = await fieldReader(token, ["2626"], loan.id);
                    let json;
                    if ((data["2626"] || "").toString().trim().toLowerCase() === "correspondent") {
                        json = await import('../../data/correspondent_business/additionalFields.json');
                    } else {
                        json = await import('../../data/business/additionalFields.json');
                    }
                    setAdditionalFields(getAdditionalInfoFields(json.default || json));
                } catch (e) {
                    const json = await import('../../data/business/additionalFields.json');
                    setAdditionalFields(getAdditionalInfoFields(json.default || json));
                }
                setLoanid(loan.id);
                setFieldsJson(encompassFields);
                setLoading(false);
                await getToken(false);
            });
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            setToken(token);
        } catch (err) {
            logError("ADDITIONAL_INFO_ONLY_TOKEN_FETCH_ERROR");
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !loanid) return;
        fetchFields();
    }, [token, loanid]);

    const fetchFields = async () => {
        const fieldIds = [...new Set([
            ...fieldsJson.map(f => f.id),
            ...additionalFields.map(field => field.id),
            "TPO.X16"
        ])];
        setLoading(true);
        try {
            const data = await fieldReader(token, fieldIds, loanid);
            const cleanData = {};
            for (const key in data) {
                let baseKey = key;
                if (baseKey.includes("#")) baseKey = baseKey.split("#")[0];
                baseKey = baseKey.replace(/\.P[2-6]$/, "");
                cleanData[baseKey] = data[key];
            }
            setFieldValues(cleanData);
            // OrgId logic for programs/visibility
            try {
                const orgId = (cleanData["TPO.X16"] || "").trim();
                if (orgId) {
                    const [programs, pageVisibility] = await Promise.all([
                        getBPCPrograms(orgId),
                        getPageFieldVisibility(orgId, "CommericalFormPage")
                    ]);
                    setBpcPrograms(programs);
                    setHiddenAdditionalFieldIds(pageVisibility?.hiddenFields || []);
                } else {
                    setBpcPrograms([]);
                    setHiddenAdditionalFieldIds([]);
                }
            } catch (err) {
                setHiddenAdditionalFieldIds([]);
            }
        } catch (err) {
            logError("ADDITIONAL_INFO_ONLY_FIELD_FETCH_ERROR");
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldId, value) => {
        setFieldValues(prev => ({ ...prev, [fieldId]: value }));
    };


    // Helper: get field value from DOM or state, supporting checkboxes/selects
    const getUiFieldValue = (rawId, fieldMeta) => {
        const escapedId = rawId.replace(/\./g, '\\.');
        const yesCheckbox = document.getElementById(`field-${rawId}-yes`);
        const noCheckbox = document.getElementById(`field-${rawId}-no`);
        // Pair of Yes/No checkboxes
        if (yesCheckbox && noCheckbox) {
            if (fieldMeta?.datatype === "YN") {
                return yesCheckbox.checked ? "true" : noCheckbox.checked ? "false" : "";
            }
            return yesCheckbox.checked ? "Y" : noCheckbox.checked ? "N" : "";
        }
        // Multiple checkboxes using same id (checkbox group)
        const groupChecks = document.querySelectorAll(`input[type="checkbox"]#field-${escapedId}`);
        if (groupChecks && groupChecks.length > 1) {
            const checked = [...groupChecks].find(cb => cb.checked);
            return checked?.value || "";
        }
        // Single input (text/select/single checkbox)
        const input = document.getElementById(`field-${rawId}`);
        if (!input) return "";
        if (input.type === 'checkbox') {
            if (fieldMeta?.datatype === "YN") return input.checked ? "true" : "false";
            if (fieldMeta?.datatype === "Y") return input.checked ? "true" : "";
            return input.checked ? (input.value || "true") : "";
        }
        if (fieldMeta?.datatype === "string" && input.tagName === "SELECT") {
            return input.options[input.selectedIndex]?.value || "";
        }
        let val = input.value || "";
        if (fieldMeta?.datatype === "money" && typeof val === "string") {
            val = val.replace(/,/g, "");
        }
        return val;
    };

    // Get field def from additionalFields first, then fieldsJson
    const getFieldDef = (id) => {
        return additionalFields.find(f => f.id === id) || fieldsJson.find(f => f.id === id);
    };

    // Normalize value for comparisons
    const normalizeValue = (fieldDef, raw) => {
        const v = (raw ?? "").toString().trim();
        if (fieldDef?.format === "YN" || fieldDef?.format === "YN_BOOL") {
            const up = v.toUpperCase();
            if (up === "Y" || up === "YES" || up === "TRUE") return "Yes";
            if (up === "N" || up === "NO" || up === "FALSE") return "No";
        }
        return v.toUpperCase();
    };

    // Helper: check emptiness with allowZero support
    const isEmptyForRule = (raw, { allowZero } = {}) => {
        if (raw === null || raw === undefined) return true;
        const v = raw.toString().trim();
        if (v === "") return true;
        if (allowZero && (v === "0" || v === "0.0" || v === "0.00")) return false;
        return false; // non-empty otherwise
    };
    // Save logic with required validation and warning toast
    const handleSave = async () => {
        // Remove previous highlights
        document.querySelectorAll('[data-required-missing-input]').forEach(el => {
            el.removeAttribute('data-required-missing-input');
            el.style.border = '';
        });


        // Find missing required fields (not readonly, not hidden)
        const missingFields = additionalFields
            .filter(f => f.required && f.readonly !== true && f.isUpdate !== false && !(hiddenAdditionalFieldIds || []).includes(f.id))
            .filter(field => {
                const value = getUiFieldValue(field.id, field);
                return value === null || value === undefined || String(value).trim() === "";
            });

        // Evaluate JSON rules for conditional required fields
        const crossErrors = validateAdditionalJsonRules();

        let firstToFocus = null;
        const allErrors = [...missingFields, ...crossErrors];

        if (allErrors.length > 0) {
            // Highlight all error fields
            allErrors.forEach(f => {
                const el = document.getElementById(`field-${f.id}`);
                if (!el) return;
                el.setAttribute('data-required-missing-input', 'true');
                el.style.setProperty('border', '1px solid red', 'important');
                if (!firstToFocus && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA')) {
                    firstToFocus = el;
                }
            });
            if (firstToFocus) {
                try { firstToFocus.focus(); } catch (e) { /* ignore */ }
            }
            setWarningToastMessage(`Please fill all required fields:<br/>${allErrors.map(f => `• ${f.message || f.name}`).join('<br/>')}`);
            setWarningToastVisible(true);
            setTimeout(() => setWarningToastVisible(false), 5000);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const updatedFields = additionalFields
                .filter(f => f.readonly !== true && f.isUpdate !== false)
                .map(f => {
                    const input = document.getElementById(`field-${f.id}`);
                    let value = input?.value ?? fieldValues[f.id] ?? "";
                    if (f.datatype === "money" && typeof value === "string") {
                        value = value.replace(/,/g, "");
                    }
                    return { id: f.id, value };
                });
            await fieldWriter(token, loanid, updatedFields);
            setShowToastText('Saved successfully...');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 4000);
            fetchFields();
        } catch (err) {
            setErrorMessage('Save failed');
            setShowErrorToast(true);
        } finally {
            setLoading(false);
        }
    };

    const validateAdditionalJsonRules = () => {
        const errors = [];
        (additionalFields || [])
            .filter(field => !(hiddenAdditionalFieldIds || []).includes(field.id))
            .forEach(field => {
                if (!Array.isArray(field.rules) || field.rules.length === 0) return;

                const thisFieldValRaw = getUiFieldValue(field.id, field);
                const thisFieldDef = getFieldDef(field.id);
                const thisFieldVal = normalizeValue(thisFieldDef, thisFieldValRaw);

                field.rules.forEach(rule => {
                    const trigger = rule?.if?.equals;
                    if (trigger == null) return;
                    const triggerNorm = normalizeValue(thisFieldDef, trigger);

                    if (thisFieldVal === triggerNorm) {
                        const targetId = rule?.then?.field;
                        if (!targetId) return;

                        const targetDef = getFieldDef(targetId) || {};
                        const targetValRaw = getUiFieldValue(targetId, targetDef);

                        // 2a) Conditional "required" rule (with optional allowZero)
                        if (rule?.then?.required === true) {
                            const allowZero = !!rule?.then?.allowZero;
                            if (isEmptyForRule(targetValRaw, { allowZero })) {
                                errors.push({
                                    id: targetId,
                                    message: rule.message || `Field ${targetId} is required.`
                                });
                                return; // skip further checks for this rule after required fail
                            }
                        }

                        // 2b) Conditional equality rule
                        if (rule?.then?.equals != null) {
                            const targetVal = normalizeValue(targetDef, targetValRaw);
                            const mustEqualNorm = normalizeValue(targetDef, rule.then.equals);
                            if (targetVal !== mustEqualNorm) {
                                errors.push({
                                    id: targetId,
                                    message: rule.message || `Field ${targetId} must equal ${rule.then.equals}.`
                                });
                            }
                        }
                    }
                });
            });

        return errors;
    };


    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText || 'Saving...'} />
            <Toast
                visible={showToast}
                type="success"
                message={showToastText}
                onClose={() => setShowToast(false)}
                animationClass="animate-fade-in-right"
            />
            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />
            <Toast
                visible={showErrorToast}
                type="error"
                message={errorMessage}
                onClose={() => setShowErrorToast(false)}
                animationClass="animate-fade-in-right"
            />
            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">Additional Information</h2>
                    <div className='loan-info-control-wrapper'>
                        <AdditionalInformation
                            additionalFields={additionalFields}
                            fieldValues={fieldValues}
                            bpcPrograms={bpcPrograms}
                            hiddenFieldIds={hiddenAdditionalFieldIds}
                            onFieldChange={handleFieldChange}
                        />
                    </div>

                    <div className={`flex gap-2 justify-end mt-4`}>

                        <button onClick={() => handleSave()} className={`btn-base btn-save ${loading ? "cursor-not-allowed opacity-60" : ""}`} disabled={loading}>
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
